import * as React from 'react'
import clx from 'classnames'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CompanyIcon } from '@/app/features/icons/Company'
import {
  CompanyType,
  isServiceCompany,
  isUnusableServiceCompany,
} from '@/app/constants/companyType'
import { useUmbrellaAccount } from '../hooks/useUmbrellaAccount'

import { SidebarMenu } from '../components/SidebarMenu'
import { SidebarMenuBtn } from '../components/SidebarMenuBtn'
import { SidebarLanguageSwitcher } from '../components/SidebarLanguageSwitcher'
import { usePermissionChecker } from '../hooks/usePermissionChecker'
import {
  AccessConfig,
  evaluateAccess,
  moduleAccess,
  useAccessContext,
} from '@/modules/shared/permissions'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCompanies } from '../query-client'
import { useQueryGroups } from '@/modules/users/pages/groups/api/useQueryGroups'
import { useQueryCurrentUserDriverId } from '@/lib/api/company/drivers'
import { useAuth } from 'react-oidc-context'

import {
  faArrowDownToLineRegular,
  faArrowDownToLineSolid,
  faArrowUpToLineRegular,
  faArrowUpToLineSolid,
  faBuildCircleXmarkRegular,
  faBuildingRegular,
  faBuildingSolid,
  faBuildingsRegular,
  faBuildingsSolid,
  faSitemap,
  faCalendarSolid,
  FaIcon,
  faFileLinesRegular,
  faFileLinesSolid,
  faMoonSolid,
  faPaperPlaneRegular,
  faPaperPlaneSolid,
  faPeopleGroupRegular,
  faPeopleGroupSolid,
  faSquareArrowLeftSolid,
  faStarRegular,
  faStarSolid,
  faTableColumnsRegular,
  faTableColumnsSolid,
  faReceiptRegular,
  faReceiptSolid,
  faFileInvoice,
} from '@/lib/fa'
import {
  faUsersSolid,
  faTruckSolid,
  faPrintSolid,
  faGearSolid,
  faArrowRightFromBracketSolid,
  faUsersRegular,
  faTruckRegular,
  faPrintRegular,
  faCalendarRegular,
  faGearRegular,
  faArrowRightFromBracketRegular,
  faUserRegular,
  faUserSolid,
} from '@/lib/fa'

import { SETTINGS_ROUTES } from '@/modules/settings/routes-typed'
import { REPORT_ROUTES } from '@/modules/exports/routes-typed'
import { VEHICLE_ROUTES } from '@/modules/vehicles/routes-typed'
import { DRIVER_ROUTES } from '@/modules/drivers/routes-typed'
import { HOME_ROUTES } from '@/modules/home/routes-typed'
import { PRINTOUT_ROUTES } from '@/modules/printouts/routes-typed'
import { IMPORTS_ROUTES } from '@/modules/imports/routes-typed'
import { TIMETABLE_ROUTES } from '@/modules/timetable/routes-typed'
import { ResponsiveLogo } from '@/app/features/icons/ResponsiveLogo'

import styles from './style.module.css'
import { useUser } from '@/modules/shared/context/UserContext'
import { USERS_ROUTES } from '@/modules/users/routes-typed'
import { ADD_COMPANY_ROUTES } from '@/modules/addCompany/routes-typed'
import { BILLING_ROUTES } from '@/modules/billing/routes-typed'
import { DATABASES_ROUTES } from '@/modules/databases/routes-typed'
import { profilePath } from '@/modules/profile'
import {
  usePersistentState,
  KEYS,
} from '@/modules/shared/hooks/usePersistentState'
import { DropdownSideBar } from '@/app/features/company-selection/DropdownSideBar'
import { buildCompanySwitchPath } from '@/modules/shared/utils/companySwitchPath'
import { useSidebarStore } from '@/modules/shared/stores/sidebarStore'
import { useShallow } from 'zustand/shallow'
import { useThemeModeStore } from '@/modules/shared/stores/themeModeStore'
import { faMoon } from '@fortawesome/free-regular-svg-icons'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { NewsBell } from '@/modules/shared/components/news'

const InactiveCompanyIcon: React.FC<{ className?: string }> = ({
  className,
}) => <FaIcon icon={faBuildCircleXmarkRegular} className={className} />

const ServiceCompanyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FaIcon icon={faSitemap} className={className} />
)

export const AppSidebarContainer = () => {
  const { collapsed, toggle: toggleCollapsed } = useSidebarStore(
    useShallow((s) => ({ collapsed: s.collapsed, toggle: s.toggle })),
  )
  const { mode: themeMode, toggle: toggleThemeMode } = useThemeModeStore(
    useShallow((s) => ({ mode: s.mode, toggle: s.toggle })),
  )
  const [printoutsExpanded, setPrintoutsExpanded] = React.useState(false)
  const { t, tSafe, languages, setActiveLanguage, activeLanguage } =
    useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const cid = companyId
  const location = useLocation()
  const navigate = useNavigate()
  const can = usePermissionChecker(companyId)
  const { ctx: access } = useAccessContext(companyId)
  // Kierowcy chowamy moduły, których guard i tak nie wpuści; reszcie bramki zostają wyłączone (TODO niżej).
  const isDriverOnly = access.isDriverOnly()
  const showFor = (config: AccessConfig) =>
    !isDriverOnly || evaluateAccess(access, config)
  const { data: ownDriverId } = useQueryCurrentUserDriverId({
    companyId,
    enabled: isDriverOnly,
  })

  const { getRoleNamesInCompany, isAdmin, accountData } = useUser()
  // Shown only to users who belong to a service account (member or owner) — a flag already on
  // the account payload, so no extra request.
  const hasServiceAccount = accountData?.hasServiceAccountAccess === true
  const auth = useAuth()
  const companiesQuery = useQueryCompanies({ includeDeleted: false })
  const groupsQuery = useQueryGroups()

  const companiesOptions = companiesQuery.data?.length
    ? companiesQuery.data.map((company) => {
        const name = company.invoiceCompanyName || company.companyName || ''
        const isService = company.type === CompanyType.SERVICE
        // The marker goes in `content` (the list), not `label` — the collapsed trigger
        // renders `label` in ~150px and the suffix would eat the name. In the trigger the
        // buildings icon carries the marker instead. Search still matches on `label`.
        return {
          label: name,
          content: isService
            ? `${name} · ${tSafe('serviceAccount.title', 'Konto usługowe')}`
            : undefined,
          value: company.id,
          icon: isService
            ? ServiceCompanyIcon
            : company.isActive === false
              ? InactiveCompanyIcon
              : CompanyIcon,
        }
      })
    : []

  const selectedCompanyData = companiesQuery.data?.find(
    (c) => String(c.id) === String(companyId),
  )
  // The service company has no tachograph database, so every operational module
  // would 500. Only the config-api screens stay reachable in its context.
  const isServiceContext = isUnusableServiceCompany(selectedCompanyData)
  // A company promoted to "konto usługowe" by an admin keeps its database and full nav;
  // it only gains the account entries below.
  const isServiceSelected = isServiceCompany(selectedCompanyData)
  // Children are billed on the account: no subscription page of their own.
  const { umbrella, isLoading: umbrellaLoading } = useUmbrellaAccount(companyId)
  const TriggerCompanyIcon =
    selectedCompanyData?.type === CompanyType.SERVICE
      ? ServiceCompanyIcon
      : selectedCompanyData?.isActive === false
        ? InactiveCompanyIcon
        : CompanyIcon

  const handleLogout = async () => {
    try {
      await auth.signoutRedirect()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', err)
    }
  }

  // Width rules:
  // - Base (sm and below): always narrow (w-16)
  // - md and up:
  //   - expanded (collapsed === false): wide (220px / 264px)
  //   - collapsed === true: stays narrow
  const sidebarWidthClass = clx(
    'tw-w-[54px]', // Always narrow by default (applies to all breakpoints)
    !collapsed && 'md:tw-w-[220px] 2xl:tw-w-[264px]', // Wide only on md+ when expanded
  )

  const selectedCompany = React.useMemo(() => {
    return companiesOptions.find((c) => c.value == companyId) || null
  }, [companyId, companiesOptions])

  const currentOrganization = React.useMemo(() => {
    if (!groupsQuery.data || !companyId) return null
    return (
      groupsQuery.data.find((group) =>
        group.companies.some((c) => String(c.id) === String(companyId)),
      ) || null
    )
  }, [groupsQuery.data, companyId])

  const currentRoleNames = getRoleNamesInCompany(companyId)

  // Auto-expand printouts menu when on printouts page
  React.useEffect(() => {
    if (location.pathname.includes('/printoutsModule/')) {
      setPrintoutsExpanded(true)
    }
  }, [location.pathname])

  const [storedCompanyId, setStoredCompanyId] = usePersistentState<string>(
    KEYS.SELECTED_COMPANY_ID,
    '',
    { storage: 'localStorage' },
  )

  const [lastDriverRoute, setLastDriverRoute] = usePersistentState<string>(
    'lastDriverModuleRoute',
    '',
    { storage: 'sessionStorage' },
  )

  const [lastVehicleRoute, setLastVehicleRoute] = usePersistentState<string>(
    'lastVehicleModuleRoute',
    '',
    { storage: 'sessionStorage' },
  )

  React.useEffect(() => {
    if (companyId && companyId !== storedCompanyId) {
      setStoredCompanyId(companyId)
    }
  }, [storedCompanyId, setStoredCompanyId, companyId])

  // Zapamiętane trasy niosą identyfikatory encji konkretnej firmy, więc po
  // zmianie firmy kasujemy je zamiast podmieniać sam segment `/company/<id>` —
  // przyciski wracają wtedy do listy właściwej firmy.
  React.useEffect(() => {
    if (!companyId || !lastDriverRoute) return
    const savedCompanyId = lastDriverRoute.match(/\/company\/([^/]+)/)?.[1]
    if (savedCompanyId && savedCompanyId !== companyId) {
      setLastDriverRoute('')
    }
  }, [companyId, lastDriverRoute])

  React.useEffect(() => {
    if (!companyId || !lastVehicleRoute) return
    const savedCompanyId = lastVehicleRoute.match(/\/company\/([^/]+)/)?.[1]
    if (savedCompanyId && savedCompanyId !== companyId) {
      setLastVehicleRoute('')
    }
  }, [companyId, lastVehicleRoute])

  const userId = auth.user?.profile.sub
  const prevUserIdRef = React.useRef(userId)
  React.useEffect(() => {
    if (prevUserIdRef.current && userId && prevUserIdRef.current !== userId) {
      setLastDriverRoute('')
      setLastVehicleRoute('')
    }
    prevUserIdRef.current = userId
  }, [userId])

  React.useEffect(() => {
    if (
      location.pathname.includes('/driverModule/') &&
      !location.pathname.includes('/editBusinessTrip/') &&
      !location.pathname.includes('/newGroupedBusinessTrip/') &&
      !location.pathname.includes('/addBusinessTrip')
    ) {
      setLastDriverRoute(location.pathname)
    }
  }, [location.pathname, setLastDriverRoute])

  React.useEffect(() => {
    if (location.pathname.includes('/vehicleModule/')) {
      setLastVehicleRoute(location.pathname)
    }
  }, [location.pathname, setLastVehicleRoute])

  return (
    <div
      data-test-id="app-drawer"
      className={`layer-drawer tw-relative tw-z-[290] tw-m-1 tw-flex tw-h-[calc(100vh-8px)] tw-max-h-[calc(100vh-8px)] tw-flex-col tw-items-stretch tw-justify-between tw-overflow-x-visible tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 ${sidebarWidthClass}`}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        className={clx(
          styles.iconToggleSidebar,
          'tw-hidden md:tw-flex',
          'tw-absolute tw-top-[16px] tw-z-[100]',
          'tw-h-7 tw-w-7 tw-items-center tw-justify-center',
          'tw-rounded-full tw-bg-base-100 tw-shadow',
          'tw-transition hover:tw-bg-[#E4E9F7]',
          collapsed ? 'tw-right-[-15px]' : 'tw-right-[-10px]',
        )}
      >
        <FaIcon
          icon={faSquareArrowLeftSolid}
          className={clx(
            'tw-h-4 tw-w-4 tw-text-content-base tw-transition-transform',
            collapsed && 'tw-rotate-180',
          )}
        />
      </button>
      <div className="custom-scrollbar tw-relative tw-flex-1 tw-overflow-y-auto">
        {/* Collapse/expand toggle button */}

        {/* Logo: shown from md+ (like your original), centered properly */}
        <div className="tw-flex tw-max-w-[150px] tw-items-center tw-justify-center tw-px-3 tw-pt-5">
          <ResponsiveLogo collapsed={collapsed} />
        </div>

        {/* Content container */}
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
          <SidebarMenu className="tw-flex tw-w-full tw-flex-col">
            {currentOrganization && (
              <div
                className={clx(
                  'tw-hidden tw-truncate tw-px-2 tw-pb-1 tw-text-xs tw-text-content-secondary',
                  !collapsed && 'md:tw-block',
                )}
                title={currentOrganization.name}
              >
                <span className="tw-opacity-60">
                  {t('shared.sidebar.organization')}:{' '}
                </span>
                {currentOrganization.name}
              </div>
            )}
            <DropdownSideBar
              collapsed={collapsed}
              className="tw-z-10 tw-text-base-content"
              icon={TriggerCompanyIcon}
              placeholder={t('shared.sidebar.select-company')}
              tooltip={t('shared.sidebar.company-selection')}
              options={companiesOptions}
              name="company"
              disabled={companiesQuery.isLoading}
              hideArrow={companiesOptions.length <= 1}
              value={companyId}
              onChange={(newCompanyId: string) => {
                const target = companiesQuery.data?.find(
                  (c) => String(c.id) === String(newCompanyId),
                )
                // Swapping the path segment would land the service company on
                // /dashboard, which it has no database to answer.
                if (isUnusableServiceCompany(target)) {
                  navigate(`/et/company/${newCompanyId}/serviceAccount`)
                  return
                }
                navigate(
                  buildCompanySwitchPath(
                    location.pathname,
                    newCompanyId,
                    companiesQuery.data,
                  ),
                )
              }}
            />

            {/* Everything below hits /api/{companyId}/… and therefore needs the
                company's tachograph database. Hidden in a service-company context. */}
            {!isServiceContext && (
              <>
                {!isDriverOnly && (
                  <SidebarMenuBtn
                    solidIcon={faTableColumnsSolid}
                    regularIcon={faTableColumnsRegular}
                    label="Dashboard"
                    href={HOME_ROUTES.dashboard.buildPath({ companyId: cid })}
                    collapsed={collapsed}
                  />
                )}
                {isAdmin() && (
                  <SidebarMenuBtn
                    solidIcon={faTableColumnsSolid}
                    regularIcon={faTableColumnsRegular}
                    label="Test EasyTMS"
                    href={`/et/company/${cid}/payrollTest`}
                    collapsed={collapsed}
                    activeMatchPattern="payrollTest"
                  />
                )}
                <SidebarMenuBtn
                  solidIcon={faPeopleGroupSolid}
                  regularIcon={faPeopleGroupRegular}
                  label={t('dashboard.drivers')}
                  href={
                    isDriverOnly && ownDriverId
                      ? DRIVER_ROUTES.activities.buildPath({ companyId: cid, driverId: ownDriverId })
                      : // Strip any `/month/YYYY-MM` segment so the target page
                        // re-derives the current month from the global date store
                        // (useDateStore) instead of using whatever month happened to
                        // be in the saved URL.
                        lastDriverRoute
                        ? lastDriverRoute.replace(/\/month\/[^/]+/, '')
                        : DRIVER_ROUTES.groupContracts.buildPath({ companyId: cid })
                  }
                  collapsed={collapsed}
                  activeMatchPattern="driverModule"
                />
                {showFor(moduleAccess.vehicles) && (
                  <SidebarMenuBtn
                    solidIcon={faTruckSolid}
                    regularIcon={faTruckRegular}
                    label={t('dashboard.vehicles')}
                    href={
                      lastVehicleRoute
                        ? lastVehicleRoute
                        : VEHICLE_ROUTES.list.buildPath({ companyId: cid })
                    }
                    collapsed={collapsed}
                    activeMatchPattern="vehicleModule"
                  />
                )}

                {/* Printouts with submenu */}
                {showFor(moduleAccess.printouts) && (
                  <SidebarMenuBtn
                    solidIcon={faPrintSolid}
                    regularIcon={faPrintRegular}
                    label={t('dashboard.printouts')}
                    collapsed={collapsed}
                    displayArrow={false}
                    activeMatchPattern="printoutsModule"
                    path={PRINTOUT_ROUTES.printoutList.buildPath({ companyId: cid })}
                    hasChildrenExpanded={printoutsExpanded}
                    onClick={() => setPrintoutsExpanded(!printoutsExpanded)}
                  >
                    <SidebarMenuBtn
                      hasParent
                      solidIcon={faFileLinesSolid}
                      regularIcon={faFileLinesRegular}
                      label={t('printout.list')}
                      path={PRINTOUT_ROUTES.printoutList.buildPath({
                        companyId: cid,
                      })}
                      collapsed={collapsed}
                    />
                    <SidebarMenuBtn
                      hasParent
                      solidIcon={faStarSolid}
                      regularIcon={faStarRegular}
                      label={t('printout.favourite')}
                      path={PRINTOUT_ROUTES.favoritePrintouts.buildPath({
                        companyId: cid,
                      })}
                      collapsed={collapsed}
                    />
                    <SidebarMenuBtn
                      hasParent
                      solidIcon={faPaperPlaneSolid}
                      regularIcon={faPaperPlaneRegular}
                      label={tSafe('printout.to-send', 'Do wysłania')}
                      path={PRINTOUT_ROUTES.toSendPrintouts.buildPath({
                        companyId: cid,
                      })}
                      collapsed={collapsed}
                    />
                  </SidebarMenuBtn>
                )}

                {showFor(moduleAccess.imports) && (
                  <SidebarMenuBtn
                    solidIcon={faArrowDownToLineSolid}
                    regularIcon={faArrowDownToLineRegular}
                    label={t('dashboard.import')}
                    href={IMPORTS_ROUTES.importHomeCards.buildPath({
                      companyId: cid,
                    })}
                    collapsed={collapsed}
                  />
                )}
                {showFor(moduleAccess.exports) && (
                  <SidebarMenuBtn
                    solidIcon={faArrowUpToLineSolid}
                    regularIcon={faArrowUpToLineRegular}
                    label={t('dashboard.export')}
                    href={REPORT_ROUTES.reportsList.buildPath({ companyId: cid })}
                    collapsed={collapsed}
                  />
                )}
                {showFor(moduleAccess.timetable) && (
                  <SidebarMenuBtn
                    solidIcon={faCalendarSolid}
                    regularIcon={faCalendarRegular}
                    label={t('dashboard.calendar')}
                    href={TIMETABLE_ROUTES.alertsList.buildPath({ companyId: cid })}
                    collapsed={collapsed}
                  />
                )}
                {/* TODO: re-enable permission gates once roles are fully wired up */}
                {showFor(moduleAccess.settings) && (
                  <SidebarMenuBtn
                    solidIcon={faGearSolid}
                    regularIcon={faGearRegular}
                    href={SETTINGS_ROUTES.basic.buildPath({ companyId: cid })}
                    label={t('dashboard.settings')}
                    collapsed={collapsed}
                    activeMatchPattern="configuration"
                  />
                )}
                {showFor(moduleAccess.users) && (
                  <SidebarMenuBtn
                    solidIcon={faUsersSolid}
                    regularIcon={faUsersRegular}
                    href={USERS_ROUTES.usersList.buildPath({ companyId: cid })}
                    label={t('dashboard.users')}
                    collapsed={collapsed}
                  />
                )}
                {showFor(moduleAccess.databases) && (
                  <SidebarMenuBtn
                    solidIcon={faBuildingsSolid}
                    regularIcon={faBuildingsRegular}
                    href={DATABASES_ROUTES.databases.buildPath({ companyId: cid })}
                    label={t('shared.sidebar.databases')}
                    collapsed={collapsed}
                  />
                )}
                {showFor(moduleAccess.addCompany) && (
                  <SidebarMenuBtn
                    solidIcon={faBuildingSolid}
                    regularIcon={faBuildingRegular}
                    href={ADD_COMPANY_ROUTES.addCompany.buildPath({ companyId: cid })}
                    label={t('shared.sidebar.contractors')}
                    collapsed={collapsed}
                  />
                )}
                {showFor(moduleAccess.billing) && (
                  <SidebarMenuBtn
                    solidIcon={faReceiptSolid}
                    regularIcon={faReceiptRegular}
                    href={BILLING_ROUTES.perCompany.buildPath({ companyId: cid })}
                    label={t('shared.sidebar.billing')}
                    collapsed={collapsed}
                    activeMatchPattern="billingModule"
                  />
                )}
              </>
            )}
            {/* In the service company the account entries lead; elsewhere the profile does. */}
            {isServiceSelected ? (
              <>
              {(hasServiceAccount || isServiceSelected) && (
                <SidebarMenuBtn
                  solidIcon={faSitemap}
                  regularIcon={faSitemap}
                  href={`/et/company/${cid}/serviceAccount`}
                  label="Konto usługowe"
                  collapsed={collapsed}
                  activeMatchPattern="serviceAccount"
                />
              )}
              {/* Shown for every billing provider: self-service (Przelewy24) tenants
                  manage their plan here, Manual (Claro-invoiced) companies see their
                  panel invoices and payment status. */}
              {!umbrella && !umbrellaLoading && showFor(moduleAccess.subscription) && (
                <SidebarMenuBtn
                  solidIcon={faFileInvoice}
                  regularIcon={faFileInvoice}
                  href={`/et/company/${cid}/my-subscription`}
                  label={
                    isServiceSelected
                      ? tSafe('serviceAccount.account-billing', 'Rozliczenia konta')
                      : 'Moja subskrypcja'
                  }
                  collapsed={collapsed}
                  activeMatchPattern="my-subscription"
                />
              )}
              <SidebarMenuBtn
                solidIcon={faUserSolid}
                regularIcon={faUserRegular}
                href={profilePath.replace(':companyId', String(cid))}
                label={t('dashboard.profile')}
                collapsed={collapsed}
                activeMatchPattern="profile"
              />
              </>
            ) : (
              <>
              <SidebarMenuBtn
                solidIcon={faUserSolid}
                regularIcon={faUserRegular}
                href={profilePath.replace(':companyId', String(cid))}
                label={t('dashboard.profile')}
                collapsed={collapsed}
                activeMatchPattern="profile"
              />
              {/* Shown for every billing provider: self-service (Przelewy24) tenants
                  manage their plan here, Manual (Claro-invoiced) companies see their
                  panel invoices and payment status. */}
              {!umbrella && !umbrellaLoading && showFor(moduleAccess.subscription) && (
                <SidebarMenuBtn
                  solidIcon={faFileInvoice}
                  regularIcon={faFileInvoice}
                  href={`/et/company/${cid}/my-subscription`}
                  label={
                    isServiceSelected
                      ? tSafe('serviceAccount.account-billing', 'Rozliczenia konta')
                      : 'Moja subskrypcja'
                  }
                  collapsed={collapsed}
                  activeMatchPattern="my-subscription"
                />
              )}
              {(hasServiceAccount || isServiceSelected) && (
                <SidebarMenuBtn
                  solidIcon={faSitemap}
                  regularIcon={faSitemap}
                  href={`/et/company/${cid}/serviceAccount`}
                  label="Konto usługowe"
                  collapsed={collapsed}
                  activeMatchPattern="serviceAccount"
                />
              )}
              </>
            )}
          </SidebarMenu>

          {/* Logout button pushed to bottom */}
        </div>
      </div>
      <div className="tw-pb-2">
        <div
          className={clx(
            'tw-flex tw-items-center tw-justify-center tw-px-2 tw-py-1.5',
            collapsed ? 'tw-flex-col' : 'md:tw-flex-row md:tw-justify-start',
          )}
        >
          <NewsBell collapsed={collapsed} />
        </div>

        <div
          className={clx(
            'tw-flex tw-items-center tw-justify-center tw-px-2 tw-py-1.5',
            collapsed ? 'tw-flex-col' : 'md:tw-flex-row md:tw-justify-start',
          )}
        >
          <Tooltip
            onClick={toggleThemeMode}
            text={
              themeMode === 'dark'
                ? t('shared.sidebar.theme-light') || 'Tryb jasny'
                : t('shared.sidebar.theme-dark') || 'Tryb ciemny'
            }
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-text-content-base tw-opacity-80 tw-transition-opacity tw-p-2 hover:tw-opacity-100 ${themeMode === 'dark' ? "tw-bg-primary " : "tw-bg-base-100"}`}
          >
            <FaIcon
              icon={themeMode === 'dark' ? faMoonSolid : faMoon}
              className={`tw-h-4 tw-w-4 ${themeMode === 'dark' ? 'tw-text-white' : 'tw-text-content-base'}`}
            />
          </Tooltip>
        </div>
       

        {/* Language switcher */}
        <SidebarLanguageSwitcher
          collapsed={collapsed}
          languages={languages}
          activeCode={activeLanguage?.code}
          onSelect={(code) => {
            setActiveLanguage(code)
            document.cookie = `lang=${code};path=/;max-age=31536000`
          }}
        />

        <SidebarMenuBtn
          solidIcon={faArrowRightFromBracketSolid}
          regularIcon={faArrowRightFromBracketRegular}
          label="Logout"
          onClick={handleLogout}
          collapsed={collapsed}
          logout
        />
      </div>
    </div>
  )
}
